/* How long ago something happened, as a reader says it: "3 days ago".
 *
 * **Here rather than in each feature**, because three screens built to the redesign say the same
 * thing about time — who signed a contract and when, who generated a pack, when a policy version was
 * activated — and three spellings of "an hour ago" is the two-vocabularies failure in its smallest
 * form. `instant.ts` beside it formats an instant a reader acts on; this one says how recent a past
 * event is, which is what a history line wants.
 *
 * The largest whole unit, and a future instant reads as just now: clock skew between the platform
 * and a browser is real, and "in -1 hours" is a rendering bug that looks like data. An instant it
 * cannot read yields `null`, so the caller decides what to show rather than printing "Invalid Date".
 */

import type { TFunction } from 'i18next';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export function ageText(t: TFunction, iso: string, now: Date): string | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const elapsed = now.getTime() - at.getTime();
  if (elapsed < MINUTE) return t('common.ago.justNow');
  if (elapsed < HOUR)
    return t('common.ago.minutes', { count: Math.floor(elapsed / MINUTE) });
  if (elapsed < DAY) return t('common.ago.hours', { count: Math.floor(elapsed / HOUR) });
  return t('common.ago.days', { count: Math.floor(elapsed / DAY) });
}
